import { useState, useEffect } from 'react';
import usePWA from '../hooks/usePWA';

/**
 * PWABanners — two non-blocking banners:
 *  1. "Update available" — only shown in production (where SW runs)
 *  2. "Install app"     — shown 3 s after load when prompt is ready
 */
const PWABanners = () => {
  const {
    canInstall, isInstalled,
    updateAvailable, applyUpdate,
    triggerInstall, isIOS,
  } = usePWA();

  const [showInstall, setShowInstall] = useState(false);
  const [dismissed,   setDismissed]   = useState(false);
  const [installing,  setInstalling]  = useState(false);

  // Show install banner after 3 s
  useEffect(() => {
    if ((canInstall || isIOS) && !isInstalled && !dismissed) {
      const t = setTimeout(() => setShowInstall(true), 3000);
      return () => clearTimeout(t);
    }
  }, [canInstall, isIOS, isInstalled, dismissed]);

  const handleInstall = async () => {
    if (isIOS) { window.location.href = '/install'; return; }
    setInstalling(true);
    await triggerInstall();
    setInstalling(false);
    setShowInstall(false);
  };

  const dismiss = () => { setShowInstall(false); setDismissed(true); };

  const isDev = process.env.NODE_ENV === 'development';

  return (
    <>
      {/* ── Update banner (production only) ────────────────────────────── */}
      {!isDev && updateAvailable && (
        <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between gap-2 bg-blue-600 text-white px-3 sm:px-4 py-2 text-xs sm:text-sm shadow-lg animate-fade-in">
          <div className="flex items-center gap-2">
            <span className="text-base">🔄</span>
            <span className="font-medium">A new version of GS Mwendo is available.</span>
          </div>
          <button onClick={applyUpdate}
            className="flex-shrink-0 bg-white text-blue-600 font-bold px-3 py-1 rounded-lg text-xs hover:bg-blue-50 transition-colors">
            Update now
          </button>
        </div>
      )}

      {/* ── Install banner (bottom slide-up) ───────────────────────────── */}
      {showInstall && !isInstalled && (
        <div className="fixed bottom-16 sm:bottom-4 left-3 right-3 sm:left-auto sm:right-4 sm:w-80 z-[99] install-banner">
          <div className="bg-whatsapp-darker rounded-2xl shadow-2xl border border-whatsapp-green/30 overflow-hidden">
            <div className="flex items-start gap-3 p-4">
              <div className="flex-shrink-0 w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow">
                <div className="text-center">
                  <div className="text-whatsapp-darker font-black text-xs leading-none">GS</div>
                  <div className="text-whatsapp-green font-black text-xxs leading-none">S3</div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm leading-tight">Install GS Mwendo S3</p>
                <p className="text-green-300 text-xs mt-0.5">
                  {isIOS ? 'Add to Home Screen via Safari Share ⎙' : 'Add to home screen — works offline too'}
                </p>
              </div>
              <button onClick={dismiss} className="flex-shrink-0 text-green-400 hover:text-white p-1 -mt-1 -mr-1" aria-label="Dismiss">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="flex border-t border-white/10">
              <button onClick={dismiss} className="flex-1 py-2.5 text-xs text-green-400 hover:text-white hover:bg-white/5 transition-colors font-medium">
                Not now
              </button>
              <div className="w-px bg-white/10"/>
              <button onClick={handleInstall} disabled={installing}
                className="flex-1 py-2.5 text-xs text-whatsapp-green hover:text-white hover:bg-whatsapp-green/20 transition-colors font-bold disabled:opacity-60">
                {installing ? 'Installing…' : isIOS ? 'How to install' : 'Install'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWABanners;
